import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  signal,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { uniqueId } from 'lodash';

import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'aprova-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IconComponent],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements OnInit {
  @ViewChild('appendRef', { static: true })
  public appendRef: ElementRef<HTMLDivElement> | undefined;

  @ViewChild('errorMessageRef', { static: true })
  public errorMessageRef: ElementRef<HTMLDivElement> | undefined;

  @Input()
  public id: string = uniqueId('aprova-select-');

  @Input()
  public dataTestid?: string;

  @Input()
  public label: string = '';

  @Input()
  public placeholder: string = 'Selecione...';

  @Input()
  public control = new FormControl<string>('');

  @Input()
  public labelClass: string = 'block';

  @Input()
  public selectOptions: { label: string; value: string; disabled?: boolean }[] = [];

  @Input()
  public width: string = 'auto';

  @Input()
  public height: string = '44px';

  @Input()
  public readonly: boolean = false;

  @Input()
  public squared: boolean = false;

  @Input()
  public filled: boolean = false;

  @Input()
  public shadow: boolean = false;

  @Input()
  public disabled: boolean = false;

  @Input()
  public clearButton: boolean = false;

  @Input()
  public required: boolean = false;

  @Input()
  public optionalTag: boolean = false;

  @Input()
  public error: boolean = false;

  @Input()
  public errorMessages: Record<string, string> = {};

  @Output()
  public onChange = new EventEmitter<string | number>();

  public isOpen = signal(false);

  get hasError(): boolean {
    if (this.error) return true;

    const someError = Object.values(this.control.errors || {}).some(Boolean);

    return (
      someError &&
      this.control.invalid &&
      (this.control.dirty || this.control.touched)
    );
  }

  get errorMessageInternal(): string {
    const key = Object.keys(this.control.errors || {}).find(Boolean);
    return key && this.errorMessages[key] ? this.errorMessages[key] : '';
  }

  get isAppendSlotUsed(): boolean {
    return !!this.appendRef?.nativeElement.innerHTML;
  }

  get isErrorMessageSlotUsed(): boolean {
    return !!this.errorMessageRef?.nativeElement.innerHTML;
  }

  get selectedLabel(): string {
    if (!this.control.value) return this.placeholder;
    if (this.control.value === '') return this.placeholder;

    const selected = this.selectOptions.find(
      (opt) => opt.value === this.control.value
    );
    return !selected ? this.placeholder : selected.label;
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  public clear(): void {
    this.control.setValue('');
  }

  public onHandleMenuOpen(): void {
    if (this.disabled) return;
    this.isOpen.update((prev) => !prev);
  }

  public closeMenu(): void {
    this.isOpen.set(false);
  }

  public onSelectOption(option: { label: string; value: string; disabled?: boolean }): void {
    if (option.disabled) return;
    
    this.control.setValue(option.value);
    this.onChange.emit(option.value);
    this.closeMenu();
  }
}
